import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  collection, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  orderBy, 
  limit, 
  startAfter, 
  runTransaction,
  doc as firestoreDoc
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faCar, 
  faPhone, 
  faChevronLeft, 
  faStar, 
  faStarHalfAlt, 
  faUser, 
  faCalendarAlt, 
  faGlobe, 
  faArrowLeft,
  faEye,
  faComment,
  faCheck,
  faClock,
  faPlus,
  faMinus,
  faInfo,
  faDirections,
  faStar as faSolidStar,
  faExternalLinkAlt,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import UserContext from '../../context/UserContext';
import TourContext from '../../context/TourContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import styles from './VenueProfile.module.css';
import defaultVenueImage from '../../assets/Images/defaulthotelpicture.png';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import olaLogo from '../../assets/Images/OLAlogo.png';
import uberLogo from '../../assets/Images/Uberlogo.png';
import rapidoLogo from '../../assets/Images/Rapidologo.jpg';

const VenueProfile = () => {
  const { placeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const { selectedTour, tours } = useContext(TourContext);
  
  // State for venue data
  const [venueData, setVenueData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for feedback
  const [userFeedback, setUserFeedback] = useState({
    rating: 0,
    comment: '',
    tags: []
  });
  const [feedbackList, setFeedbackList] = useState([]);
  const [lastFeedbackDoc, setLastFeedbackDoc] = useState(null);
  const [hasMoreFeedback, setHasMoreFeedback] = useState(true);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  
  // Rating stars UI state
  const [hoverRating, setHoverRating] = useState(0);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [activeTab, setActiveTab] = useState('reviews');
  const [isRideModalVisible, setIsRideModalVisible] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);

  // Define tag categories
  const tagCategories = {
    loadIn: {
      title: "Load-in & Setup",
      tags: [
        "Easy Load-In",
        "Difficult Load-In",
        "No Parking for Bus/Van",
        "Loading Dock Available",
        "Long Setup Time"
      ]
    },
    soundStage: {
      title: "Sound & Stage",
      tags: [
        "Great Monitor Mix",
        "Bad Monitor Mix",
        "Great Acoustics",
        "Bad Acoustics",
        "Spacious Stage",
        "Cramped Stage",
        "Good Lighting Rig",
        "Limited Power Outlets",
        "Professional Sound Engineer",
        "Inexperienced Crew"
      ]
    },
    backstage: {
      title: "Backstage & Hospitality",
      tags: [
        "Clean Green Room",
        "No Green Room",
        "No Shower",
        "Good Catering",
        "Poor Hospitality",
        "Late Settlement",
        "Friendly Venue Manager",
        "Tight Curfew",
        "Limited Dressing Room Space"
      ]
    },
    logistics: {
      title: "Logistics & Tech",
      tags: [
        "Solid Wi-Fi",
        "No Internet Access",
        "Provided Tech Rider",
        "Tech Issues On Site",
        "Power Backup Available",
        "Outdated Equipment"
      ]
    }
  };

  const getOptimizedImageUrl = (url) => {
    if (!url) return null;
    
    // Check if it's a Google Places image URL
    if (url.includes('googleusercontent.com/places/')) {
      // Remove size restrictions and get high resolution image
      // Replace the size parameters with a larger width
      return url.replace(/=s\d+-w\d+-h\d+/, '=s2048-w2048');
    }
    
    return url;
  };

  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load venue data from Firestore
  useEffect(() => {
    const fetchVenueData = async () => {
      if (!placeId) return;
      
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        const placeRef = firestoreDoc(db, 'Places', placeId);
        const placeDoc = await getDoc(placeRef);
        
        if (placeDoc.exists()) {
          setVenueData(placeDoc.data());
        } else {
          setError('Venue not found');
        }
      } catch (err) {
        console.error('Error fetching venue data:', err);
        setError('Failed to load venue data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchVenueData();
  }, [placeId]);
  
  // Load feedback data
  const loadFeedback = useCallback(async (isInitial = false) => {
    if (!placeId) return;
    
    setLoadingFeedback(true);
    try {
      const db = getFirestoreInstance();
      const feedbackRef = collection(db, 'Places', placeId, 'feedback');
      
      let feedbackQuery;
      if (isInitial) {
        feedbackQuery = query(
          feedbackRef,
          orderBy('timestamp', 'desc'),
          limit(6)
        );
      } else if (lastFeedbackDoc) {
        feedbackQuery = query(
          feedbackRef,
          orderBy('timestamp', 'desc'),
          startAfter(lastFeedbackDoc),
          limit(6)
        );
      } else {
        // No more feedback to load
        setHasMoreFeedback(false);
        setLoadingFeedback(false);
        return;
      }
      
      const feedbackSnapshot = await getDocs(feedbackQuery);
      
      // Update hasMoreFeedback state
      setHasMoreFeedback(feedbackSnapshot.docs.length === 6);
      
      if (feedbackSnapshot.empty) {
        setHasMoreFeedback(false);
      } else {
        // Get the last document
        setLastFeedbackDoc(feedbackSnapshot.docs[feedbackSnapshot.docs.length - 1]);
        
        // Process feedback and fetch user data
        const feedbackData = await Promise.all(
          feedbackSnapshot.docs.map(async (feedbackDoc) => {
            const data = feedbackDoc.data();
            
            // Try to get user data from cache first
            const userCacheKey = `venueProfileUserCache_${data.userId}`;
            let userDataObj = null;
            
            try {
              const cachedData = localStorage.getItem(userCacheKey);
              if (cachedData) {
                const parsedCache = JSON.parse(cachedData);
                // Check if cache is still valid (7 days)
                if (Date.now() - parsedCache.timestamp < 7 * 24 * 60 * 60 * 1000) {
                  userDataObj = parsedCache.data;
                  return {
                    id: feedbackDoc.id,
                    ...data,
                    user: userDataObj
                  };
                }
              }
              
              // If no valid cache, fetch from Firestore
              const userDocRef = firestoreDoc(db, 'users', data.userId);
              const userDoc = await getDoc(userDocRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                userDataObj = {
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
                
                // Save to cache
                localStorage.setItem(userCacheKey, JSON.stringify({
                  data: userDataObj,
                  timestamp: Date.now()
                }));
              }
              
              return {
                id: feedbackDoc.id,
                ...data,
                user: userDataObj || {
                  fullName: 'Unknown User',
                  userName: '',
                  profilePictureUrl: '',
                  userSubType: '',
                  idVerificationStatus: ''
                }
              };
            } catch (err) {
              console.error('Error fetching user data:', err);
              
              return {
                id: feedbackDoc.id,
                ...data,
                user: {
                  fullName: 'Unknown User',
                  userName: '',
                  profilePictureUrl: '',
                  userSubType: '',
                  idVerificationStatus: ''
                }
              };
            }
          })
        );
        
        if (isInitial) {
          setFeedbackList(feedbackData);
        } else {
          setFeedbackList(prev => [...prev, ...feedbackData]);
        }
      }
    } catch (err) {
      console.error('Error loading feedback:', err);
      setHasMoreFeedback(false);
    } finally {
      setLoadingFeedback(false);
    }
  }, [placeId, lastFeedbackDoc]);
  
  // Load initial feedback
  useEffect(() => {
    let mounted = true;
    
    if (venueData && mounted) {
      loadFeedback(true);
    }
    
    return () => {
      mounted = false;
    };
  }, [venueData]); // Remove loadFeedback from dependencies

  // Handle tag selection
  const handleTagSelect = (tag) => {
    setUserFeedback(prev => {
      const tags = [...prev.tags];
      const tagIndex = tags.indexOf(tag);
      
      if (tagIndex >= 0) {
        // Remove tag if already selected
        tags.splice(tagIndex, 1);
      } else {
        // Add tag
        tags.push(tag);
      }
      
      return { ...prev, tags };
    });
  };
  
  // Handle rating selection
  const handleRatingClick = (rating) => {
    setUserFeedback(prev => ({ ...prev, rating }));
  };
  
  // Handle comment input
  const handleCommentChange = (e) => {
    const comment = e.target.value;
    if (comment.length <= 1000) {
      setUserFeedback(prev => ({ ...prev, comment }));
    }
  };
  
  // Submit feedback
  const handleSubmitFeedback = async () => {
    if (!userData?.userId || !placeId || !userFeedback.rating) {
      alert('Please sign in and provide a rating before submitting feedback');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const db = getFirestoreInstance();
      const placeRef = firestoreDoc(db, 'Places', placeId);
      
      // Create feedback document
      const feedbackData = {
        userId: userData.userId,
        rating: userFeedback.rating,
        comment: userFeedback.comment,
        tags: userFeedback.tags,
        timestamp: new Date().toISOString()
      };
      
      // Run transaction to update place document and add feedback
      await runTransaction(db, async (transaction) => {
        const placeDoc = await transaction.get(placeRef);
        
        if (!placeDoc.exists()) {
          throw new Error('Venue not found');
        }
        
        const placeData = placeDoc.data();
        
        // Calculate new rating statistics
        const ratingField = userFeedback.rating === 0.5 ? 'halfStar' :
                            userFeedback.rating === 1 ? 'oneStar' :
                            userFeedback.rating === 1.5 ? 'oneHalfStar' :
                            userFeedback.rating === 2 ? 'twoStar' :
                            userFeedback.rating === 2.5 ? 'twoHalfStar' :
                            userFeedback.rating === 3 ? 'threeStar' :
                            userFeedback.rating === 3.5 ? 'threeHalfStar' :
                            userFeedback.rating === 4 ? 'fourStar' :
                            userFeedback.rating === 4.5 ? 'fourHalfStar' :
                            'fiveStar';
        
        const totalRatingsCount = (placeData.totalRatingsCount || 0) + 1;
        const totalRatingsScore = (placeData.totalRatingsScore || 0) + userFeedback.rating;
        const averageRating = totalRatingsScore / totalRatingsCount;
        
        // Create an update object with only the rating-related fields
        const updateObj = {
          [ratingField]: (placeData[ratingField] || 0) + 1,
          totalRatingsCount,
          totalRatingsScore,
          averageRating
        };
        
        // Update place document with new ratings only
        transaction.update(placeRef, updateObj);
        
        // Add feedback to subcollection
        const feedbackColRef = collection(placeRef, 'feedback');
        const newFeedbackRef = firestoreDoc(feedbackColRef);
        transaction.set(newFeedbackRef, feedbackData);
      });
      
      // Update local state
      setVenueData(prev => {
        const ratingField = userFeedback.rating === 0.5 ? 'halfStar' :
                            userFeedback.rating === 1 ? 'oneStar' :
                            userFeedback.rating === 1.5 ? 'oneHalfStar' :
                            userFeedback.rating === 2 ? 'twoStar' :
                            userFeedback.rating === 2.5 ? 'twoHalfStar' :
                            userFeedback.rating === 3 ? 'threeStar' :
                            userFeedback.rating === 3.5 ? 'threeHalfStar' :
                            userFeedback.rating === 4 ? 'fourStar' :
                            userFeedback.rating === 4.5 ? 'fourHalfStar' :
                            'fiveStar';
                            
        const totalRatingsCount = (prev.totalRatingsCount || 0) + 1;
        const totalRatingsScore = (prev.totalRatingsScore || 0) + userFeedback.rating;
        
        return {
          ...prev,
          [ratingField]: (prev[ratingField] || 0) + 1,
          totalRatingsCount,
          totalRatingsScore,
          averageRating: totalRatingsScore / totalRatingsCount
        };
      });
      
      // Add new feedback to the top of the list
      setFeedbackList(prev => [{
        id: 'new-feedback',
        ...feedbackData,
        user: {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userSubType: userData.userSubType || '',
          idVerificationStatus: userData.idVerificationStatus || ''
        }
      }, ...prev]);
      
      // Reset form
      setUserFeedback({
        rating: 0,
        comment: '',
        tags: []
      });
      
      // Hide feedback form
      setShowFeedbackForm(false);
      
      alert('Thank you for your feedback!');
    } catch (err) {
      console.error('Error submitting feedback:', err);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle ride booking
  const handleGetARide = () => {
    setIsRideModalVisible(true);
  };
  
  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by your browser');
        reject('Geolocation not supported');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => {
          switch(error.code) {
            case error.PERMISSION_DENIED:
              alert('Location permission denied. Please allow location access and try again.');
              break;
            case error.POSITION_UNAVAILABLE:
              alert('Location information is unavailable. Please check your device settings.');
              break;
            case error.TIMEOUT:
              alert('Location request timed out. Please try again.');
              break;
            default:
              alert('Unable to get your location. Please check your browser settings.');
          }
          reject(error);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 15000, 
          maximumAge: 0 
        }
      );
    });
  };
  
  const handleOpenCabApp = async (cabService) => {
    if (!venueData || !venueData.location) {
      alert('Venue location data is not available');
      return;
    }

    if (!venueData.location.latitude || !venueData.location.longitude) {
      alert('Invalid venue location data');
      return;
    }

    setIsLocationLoading(true);
    try {
      // Get precise current location
      const position = await getCurrentPosition();
      if (!position) {
        setIsLocationLoading(false);
        return;
      }

      const { latitude: currentLat, longitude: currentLng } = position.coords;
      const venueLat = venueData.location.latitude;
      const venueLng = venueData.location.longitude;
      const venueName = venueData.displayName || 'Venue';
      const venueAddress = venueData.formattedAddress || '';
      const fullVenueAddress = `${venueName}, ${venueAddress}`;

      let url;
      
      if (cabService === 'ola') {
        // OLA URL structure
        url = `https://book.olacabs.com/?pickup_name=Current%20Location&pickup_lat=${currentLat}&pickup_lng=${currentLng}&drop_lat=${venueLat}&drop_lng=${venueLng}&drop_name=${encodeURIComponent(fullVenueAddress)}`;
      } else if (cabService === 'uber') {
        // Uber URL structure - using simple approach
        url = `https://m.uber.com/looking?pickup_lat=${currentLat}&pickup_lng=${currentLng}&dropoff_lat=${venueLat}&dropoff_lng=${venueLng}`;
      } else if (cabService === 'rapido') {
        // For Rapido, we need to get the current address using reverse geocoding
        let currentAddress = "Current Location";
        
        try {
          // Try to get address using reverse geocoding
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${currentLat}&lon=${currentLng}&format=json`);
          const data = await response.json();
          
          if (data && data.display_name) {
            currentAddress = data.display_name;
          } else {
            // Fallback: use coordinates in the address
            currentAddress = `Location at ${currentLat.toFixed(6)}, ${currentLng.toFixed(6)}`;
          }
        } catch (geocodeError) {
          console.error('Reverse geocoding failed:', geocodeError);
          // Use coordinates as fallback
          currentAddress = `Location at ${currentLat.toFixed(6)}, ${currentLng.toFixed(6)}`;
        }
        
        url = `https://m.rapido.bike/unup-home/seo/${encodeURIComponent(currentAddress)}/${encodeURIComponent(fullVenueAddress)}?version=v3`;
      }

      // Open the URL directly
      window.open(url, '_blank');
      
    } catch (error) {
      console.error('Error getting location:', error);
      alert('Unable to get your current location. Please check your device settings and try again.');
    } finally {
      setIsLocationLoading(false);
      setIsRideModalVisible(false);
    }
  };

  // Render star rating
  const renderStarRating = (rating, size = 'md') => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesomeIcon 
          key={`full-${i}`} 
          icon={faStar} 
          className={`${styles.star} ${styles[`star-${size}`]}`} 
        />
      );
    }
    
    // Half star
    if (hasHalfStar) {
      stars.push(
        <FontAwesomeIcon 
          key="half" 
          icon={faStarHalfAlt} 
          className={`${styles.star} ${styles[`star-${size}`]}`} 
        />
      );
    }
    
    // Empty stars
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <FontAwesomeIcon 
          key={`empty-${i}`} 
          icon={farStar} 
          className={`${styles.emptyStar} ${styles[`star-${size}`]}`} 
        />
      );
    }
    
    return stars;
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  // Handle navigation
  const handleGoBack = () => {
    if (location.state?.previousPath) {
      navigate(location.state.previousPath);
    } else {
      navigate(-1);
    }
  };
  
  // Handle tour selection
  const handleTourSelect = (tour) => {
    // This function is needed for RightMenu
  };

  if (isLoading) {
    return (
      <div className={styles.layout}>
       
        <div className={styles.mainLayout}>
          {!isMobile && <LeftMenu selectedTour={selectedTour} />}
          {!isMobile && (
            <RightMenu
              selectedTour={selectedTour}
              tours={tours}
              onTourSelect={handleTourSelect}
            />
          )}
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading venue details...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.layout}>
        
        <div className={styles.mainLayout}>
          {!isMobile && <LeftMenu selectedTour={selectedTour} />}
          {!isMobile && (
            <RightMenu
              selectedTour={selectedTour}
              tours={tours}
              onTourSelect={handleTourSelect}
            />
          )}
          <div className={styles.errorContainer}>
            <h2>Error</h2>
            <p>{error}</p>
            <button onClick={handleGoBack} className={styles.backButton}>
              <FontAwesomeIcon icon={faArrowLeft} /> Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!venueData) {
    return null;
  }
  
  const {
    displayName,
    formattedAddress,
    placeCoverPhotoUrl,
    googleMapsUri,
    nationalPhoneNumber,
    websiteUri,
    averageRating = 0,
    totalRatingsCount = 0,
    types = [],
    businessStatus,
    primaryTypeDisplayName,
    accessibilityOptions,
    utcOffsetMinutes,
    userRatingCount,
    rating: googleRating
  } = venueData;
  
  // Format the business hours if available
  const renderBusinessHours = () => {
    if (venueData.regularOpeningHours && venueData.regularOpeningHours !== "undefined undefined - undefined undefined") {
      return venueData.regularOpeningHours;
    }
    return 'Hours not available';
  };
  
  // Render review content
  const renderReviewsTab = () => {
    return (
      <div className={styles.tabContent}>
        {/* Add Review Button */}
        {userData && !showFeedbackForm && (
          <button 
            className={styles.addReviewButton}
            onClick={() => setShowFeedbackForm(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Add Your Review</span>
          </button>
        )}
        
        {/* Feedback Form */}
        {showFeedbackForm && (
          <div className={styles.feedbackFormSection}>
            <div className={styles.feedbackFormHeader}>
              <h3>Write a Review</h3>
              <button 
                className={styles.closeFormButton}
                onClick={() => setShowFeedbackForm(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className={styles.feedbackForm}>
              {/* Rating */}
              <div className={styles.ratingSelector}>
                <p className={styles.ratingLabel}>Your Rating:</p>
                <div className={styles.ratingStars}>
                  {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map(rating => (
                    <button
                      key={rating}
                      className={styles.ratingButton}
                      onClick={() => handleRatingClick(rating)}
                      onMouseEnter={() => setHoverRating(rating)}
                      onMouseLeave={() => setHoverRating(0)}
                    >
                      {/* Half-star positions */}
                      {rating % 1 !== 0 ? (
                        <>
                          <div className={styles.halfStarContainer}>
                            <FontAwesomeIcon 
                              icon={
                                (hoverRating || userFeedback.rating) >= rating - 0.5
                                  ? faSolidStar 
                                  : farStar
                              } 
                              className={styles.halfStarLeft}
                            />
                            <FontAwesomeIcon 
                              icon={
                                (hoverRating || userFeedback.rating) >= rating
                                  ? faSolidStar 
                                  : farStar
                              } 
                              className={styles.halfStarRight}
                            />
                          </div>
                        </>
                      ) : (
                        <FontAwesomeIcon 
                          icon={
                            (hoverRating || userFeedback.rating) >= rating
                              ? faSolidStar 
                              : farStar
                          } 
                          className={styles.star}
                        />
                      )}
                    </button>
                  ))}
                </div>
                <span className={styles.selectedRating}>
                  {userFeedback.rating > 0 ? `${userFeedback.rating.toFixed(1)} Stars` : 'Select Rating'}
                </span>
              </div>
              
              {/* Comment */}
              <div className={styles.commentSection}>
                <textarea
                  placeholder="Share your experience at this venue (optional, max 1000 characters)"
                  value={userFeedback.comment}
                  onChange={handleCommentChange}
                  className={styles.commentInput}
                  maxLength={1000}
                />
                <div className={styles.characterCount}>
                  {userFeedback.comment.length}/1000
                </div>
              </div>
              
              {/* Tags */}
              <div className={styles.tagsSection}>
                <h3 className={styles.tagsTitle}>Select Tags (Optional)</h3>
                
                {/* Tag Categories */}
                <div className={styles.tagCategoriesContainer}>
                  {Object.entries(tagCategories).map(([key, category]) => (
                    <div key={key} className={styles.tagCategory}>
                      <h4 className={styles.categoryTitle}>{category.title}</h4>
                      <div className={styles.tagsList}>
                        {category.tags.map(tag => (
                          <button
                            key={tag}
                            className={`${styles.tagButton} ${userFeedback.tags.includes(tag) ? styles.tagSelected : ''}`}
                            onClick={() => handleTagSelect(tag)}
                          >
                            {tag}
                            {userFeedback.tags.includes(tag) && (
                              <FontAwesomeIcon icon={faCheck} className={styles.tagCheckIcon} />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Submit Button */}
              <button
                className={styles.submitButton}
                onClick={handleSubmitFeedback}
                disabled={isSubmitting || !userFeedback.rating}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}
        
        {/* Feedback List */}
        <div className={styles.feedbackListSection}>
          <h3 className={styles.sectionTitle}>
            Reviews ({totalRatingsCount})
          </h3>
          
          {feedbackList.length === 0 ? (
            <div className={styles.noFeedback}>
              <p>No reviews yet. Be the first to leave feedback!</p>
            </div>
          ) : (
            <>
              <div className={styles.feedbackList}>
                {feedbackList.map((feedback) => (
                  <div key={feedback.id} className={styles.feedbackItem}>
                    <div className={styles.feedbackHeader}>
                      <img
                        src={feedback.user.profilePictureUrl || defaultUserImage}
                        alt={feedback.user.fullName}
                        className={styles.userAvatar}
                      />
                      <div className={styles.userInfo}>
                        <div className={styles.userName}>
                          {feedback.user.fullName}
                          {feedback.user.idVerificationStatus === 'Verified' && (
                            <img 
                              src={verifiedBadge} 
                              alt="Verified" 
                              className={styles.verifiedBadge} 
                            />
                          )}
                        </div>
                        <div className={styles.userDetails}>
                          @{feedback.user.userName} • {feedback.user.userSubType}
                        </div>
                      </div>
                      <div className={styles.feedbackDate}>
                        {formatDate(feedback.timestamp)}
                      </div>
                    </div>
                    
                    <div className={styles.feedbackRating}>
                      {renderStarRating(feedback.rating, 'sm')}
                    </div>
                    
                    {feedback.comment && (
                      <div className={styles.feedbackComment}>
                        {feedback.comment}
                      </div>
                    )}
                    
                    {feedback.tags && feedback.tags.length > 0 && (
                      <div className={styles.feedbackTags}>
                        {feedback.tags.map(tag => (
                          <span key={tag} className={styles.feedbackTag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {hasMoreFeedback ? (
                <button
                  className={styles.loadMoreButton}
                  onClick={() => loadFeedback(false)}
                  disabled={loadingFeedback}
                >
                  {loadingFeedback ? 'Loading...' : 'Load More Reviews'}
                </button>
              ) : feedbackList.length > 0 && (
                <div className={styles.noMoreReviews}>
                  No more reviews to load
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };
  
  // Render map tab
  const renderMapTab = () => {
    return (
      <div className={styles.tabContent}>
        <div className={styles.mapTabHeader}>
          <h3 className={styles.mapTabTitle}>Location Map</h3>
          {googleMapsUri && (
            <a 
              href={googleMapsUri} 
              target="_blank" 
              rel="noopener noreferrer" 
              className={styles.openInMapsLink}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              <span>Open in Google Maps</span>
            </a>
          )}
        </div>
        
        <div className={styles.mapTabContent}>
          <div className={styles.addressCard}>
            <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.addressIcon} />
            <div className={styles.addressText}>
              <div className={styles.venueName}>{displayName}</div>
              <div className={styles.venueAddress}>{formattedAddress}</div>
            </div>
          </div>
          
          <div className={styles.mapContainer}>
            {venueData.location && (
              <iframe
                src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_MAPS_EMBED_KEY}&q=${encodeURIComponent(formattedAddress)}&zoom=15`}
                className={styles.map}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Map of ${displayName}`}
              />
            )}
          </div>
          
          <div className={styles.directionsButton}>
            <button 
              onClick={() => {
                if (venueData.location) {
                  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(formattedAddress)}`;
                  window.open(directionsUrl, '_blank');
                }
              }}
              className={styles.getDirectionsButton}
            >
              <FontAwesomeIcon icon={faDirections} />
              <span>Get Directions</span>
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Render info tab
  const renderInfoTab = () => {
    return (
      <div className={styles.tabContent}>
        <div className={styles.infoSection}>
          <h3 className={styles.infoSectionTitle}>Venue Information</h3>
          
          <div className={styles.infoGrid}>
            {primaryTypeDisplayName && (
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Type</div>
                <div className={styles.infoValue}>{primaryTypeDisplayName}</div>
              </div>
            )}
            
            {businessStatus && (
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Status</div>
                <div className={styles.infoValue}>
                  {businessStatus === 'OPERATIONAL' ? 'Open' : businessStatus}
                </div>
              </div>
            )}
            
            {googleRating && (
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Google Rating</div>
                <div className={styles.infoValue}>
                  {googleRating} ★ ({userRatingCount || 0} ratings)
                </div>
              </div>
            )}
            
            {venueData.regularOpeningHours && venueData.regularOpeningHours !== "undefined undefined - undefined undefined" && (
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Hours</div>
                <div className={styles.infoValue}>{renderBusinessHours()}</div>
              </div>
            )}
            
            {nationalPhoneNumber && (
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Phone</div>
                <div className={styles.infoValue}>
                  <a href={`tel:${nationalPhoneNumber}`}>{nationalPhoneNumber}</a>
                </div>
              </div>
            )}
            
            {websiteUri && (
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Website</div>
                <div className={styles.infoValue}>
                  <a href={websiteUri} target="_blank" rel="noopener noreferrer">
                    {websiteUri.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {accessibilityOptions && Object.keys(accessibilityOptions).length > 0 && (
          <div className={styles.infoSection}>
            <h3 className={styles.infoSectionTitle}>Accessibility</h3>
            <div className={styles.accessibilityList}>
              {Object.entries(accessibilityOptions).map(([key, value]) => (
                value && (
                  <div key={key} className={styles.accessibilityItem}>
                    <FontAwesomeIcon icon={faCheck} className={styles.accessibilityIcon} />
                    <span>{key.replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())
                      .replace(/([A-Z])[a-z]/g, str => str.toLowerCase())}</span>
                  </div>
                )
              ))}
            </div>
          </div>
        )}
        
        {types && types.length > 0 && (
          <div className={styles.infoSection}>
            <h3 className={styles.infoSectionTitle}>Categories</h3>
            <div className={styles.typesList}>
              {types.map(type => (
                <span key={type} className={styles.typeTag}>
                  {type.replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.layout}>
   
      <div className={styles.mainLayout}>
        {!isMobile && <LeftMenu selectedTour={selectedTour} />}
        {!isMobile && (
          <RightMenu
            selectedTour={selectedTour}
            tours={tours}
            onTourSelect={handleTourSelect}
          />
        )}
        
        <div className={styles.venueProfileContainer}>
          {/* Header with back button */}
          <div className={styles.header}>
            <button onClick={handleGoBack} className={styles.backButton}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          </div>
          
          {/* Venue hero section */}
          <div className={styles.venueHero}>
  <img 
    src={getOptimizedImageUrl(placeCoverPhotoUrl) || defaultVenueImage} 
    alt={displayName} 
    className={styles.venueImage}
    onClick={() => setIsImageModalVisible(true)}
  />
  <div className={styles.venueOverlay}>
    <h1 className={styles.venueName}>{displayName}</h1>
    <p className={styles.venueAddress}>{formattedAddress}</p>
    
    <div className={styles.venueRating}>
      <div className={styles.ratingStars}>
        {renderStarRating(averageRating)}
      </div>
      <span className={styles.ratingText}>
        {averageRating.toFixed(1)} ({totalRatingsCount} {totalRatingsCount === 1 ? 'review' : 'reviews'})
      </span>
    </div>
  </div>
</div>
          
          {/* Actions */}
          <div className={styles.venueActions}>
            {googleMapsUri && (
              <a 
                href={googleMapsUri} 
                target="_blank" 
                rel="noopener noreferrer" 
                className={styles.actionButton}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>View Map</span>
              </a>
            )}
            
            {nationalPhoneNumber && (
              <a 
                href={`tel:${nationalPhoneNumber}`} 
                className={styles.actionButton}
              >
                <FontAwesomeIcon icon={faPhone} />
                <span>Call</span>
              </a>
            )}
            
            <button
              onClick={handleGetARide}
              className={styles.actionButton}
            >
              <FontAwesomeIcon icon={faCar} />
              <span>Get a Ride</span>
            </button>
          </div>
          
          {/* Tabs Navigation */}
          <div className={styles.tabsContainer}>
            <div className={styles.tabsNav}>
              <button 
                className={`${styles.tabButton} ${activeTab === 'reviews' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('reviews')}
              >
                <FontAwesomeIcon icon={faStar} />
                <span>Reviews</span>
              </button>
              <button 
                className={`${styles.tabButton} ${activeTab === 'map' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('map')}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>Map</span>
              </button>
              <button 
                className={`${styles.tabButton} ${activeTab === 'info' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('info')}
              >
                <FontAwesomeIcon icon={faInfo} />
                <span>Details</span>
              </button>
            </div>
            
            {/* Tab Content */}
            <div className={styles.tabContentWrapper}>
              {activeTab === 'reviews' && renderReviewsTab()}
              {activeTab === 'map' && renderMapTab()}
              {activeTab === 'info' && renderInfoTab()}
            </div>
          </div>
          
          {/* Ride Modal */}
          {isRideModalVisible && (
            <div className={styles.rideModalOverlay} onClick={() => setIsRideModalVisible(false)}>
              <div className={styles.rideModalContent} onClick={e => e.stopPropagation()}>
                <div className={styles.rideModalHeader}>
                  <h3 className={styles.rideModalTitle}>Select Cab Service</h3>
                </div>
                <button 
                  className={styles.rideOptionButton}
                  onClick={() => handleOpenCabApp('ola')}
                  disabled={isLocationLoading}
                >
                  <img src={olaLogo} alt="OLA" className={styles.rideOptionLogo} />
                  <span className={styles.rideOptionText}>OLA</span>
                </button>
                <button 
                  className={styles.rideOptionButton}
                  onClick={() => handleOpenCabApp('uber')}
                  disabled={isLocationLoading}
                >
                  <img src={uberLogo} alt="UBER" className={styles.rideOptionLogo} />
                  <span className={styles.rideOptionText}>UBER</span>
                </button>
                <button 
                  className={styles.rideOptionButton}
                  onClick={() => handleOpenCabApp('rapido')}
                  disabled={isLocationLoading}
                >
                  <img src={rapidoLogo} alt="RAPIDO" className={styles.rideOptionLogo} />
                  <span className={styles.rideOptionText}>RAPIDO</span>
                </button>
              </div>
            </div>
          )}

          {/* Image Modal */}
{isImageModalVisible && (
  <div className={styles.imageModalOverlay} onClick={() => setIsImageModalVisible(false)}>
    <div className={styles.imageModalContent} onClick={e => e.stopPropagation()}>
      <button 
        className={styles.imageModalClose}
        onClick={() => setIsImageModalVisible(false)}
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
      <img 
        src={getOptimizedImageUrl(placeCoverPhotoUrl) || defaultVenueImage} 
        alt={displayName} 
        className={styles.imageModalImg}
      />
      <div className={styles.imageModalCaption}>
        <h3>{displayName}</h3>
        <p>{formattedAddress}</p>
      </div>
    </div>
  </div>
)}
          
          {/* Loading Overlay */}
          {isLocationLoading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}></div>
              <div className={styles.loadingText}>
                Getting current location...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenueProfile;